/**
 * RegionMap - Generic SVG choropleth map for US/CA/UK (and future) regions
 * Loads map path data from /maps/*.json at runtime (kept out of the JS bundle)
 */

import React, { useEffect, useState } from 'react';
import type { MarketplaceCode } from '../../types/transaction';

export interface USStateMapData {
  value: number;
  label: string;
  orders?: number;
  revenue?: number;
  profit?: number;
  margin?: number;
}

interface MapData {
  viewBox: string;
  regions: Record<string, { name?: string; abbreviation?: string; path: string }>;
}

interface RegionMapProps {
  marketplace: MarketplaceCode;
  stateData: Record<string, USStateMapData>;
  colorMode?: 'revenue' | 'profit';
  formatMoney?: (n: number) => string;
}

const BLUE_SCALE = ['#eff6ff', '#bfdbfe', '#93c5fd', '#60a5fa', '#3b82f6', '#1d4ed8'];
const PROFIT_SCALE = ['#ef4444', '#f87171', '#fbbf24', '#a3e635', '#22c55e', '#16a34a'];

const MAP_FILES: Partial<Record<MarketplaceCode, string>> = {
  US: '/maps/us-states.json',
  CA: '/maps/ca-provinces.json',
  UK: '/maps/uk-regions.json',
};

// Module-level cache so each JSON is fetched at most once per session
const cache = new Map<string, Promise<MapData>>();

const loadMap = (url: string): Promise<MapData> => {
  const cached = cache.get(url);
  if (cached) return cached;
  const p = fetch(url).then(r => {
    if (!r.ok) throw new Error(`Failed to load ${url}: ${r.status}`);
    return r.json() as Promise<MapData>;
  });
  cache.set(url, p);
  // Clear cache on fetch failure so a retry can re-attempt
  p.catch(() => cache.delete(url));
  return p;
};

const getRevenueColor = (value: number, maxValue: number): string => {
  if (value === 0 || maxValue === 0) return '#f1f5f9';
  const ratio = value / maxValue;
  const index = Math.min(Math.floor(ratio * BLUE_SCALE.length), BLUE_SCALE.length - 1);
  return BLUE_SCALE[index];
};

const getProfitColor = (margin: number): string => {
  if (margin <= -10) return PROFIT_SCALE[0];
  if (margin <= 0) return PROFIT_SCALE[1];
  if (margin <= 10) return PROFIT_SCALE[2];
  if (margin <= 20) return PROFIT_SCALE[3];
  if (margin <= 30) return PROFIT_SCALE[4];
  return PROFIT_SCALE[5];
};

const RegionMap: React.FC<RegionMapProps> = ({ marketplace, stateData, colorMode = 'revenue', formatMoney }) => {
  const url = MAP_FILES[marketplace];
  const [data, setData] = useState<MapData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hovered, setHovered] = useState<string | null>(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  useEffect(() => {
    if (!url) {
      setData(null);
      setError(null);
      return;
    }
    let cancelled = false;
    setError(null);
    setData(null);
    loadMap(url)
      .then(d => { if (!cancelled) setData(d); })
      .catch(e => { if (!cancelled) setError(e.message || 'Failed to load map'); });
    return () => { cancelled = true; };
  }, [url]);

  if (!url) return null;

  if (error) {
    return <div className="text-xs text-red-500 py-8 text-center">Map data unavailable: {error}</div>;
  }

  if (!data) {
    return (
      <div className="w-full aspect-[3/2] bg-slate-50 rounded-lg flex items-center justify-center">
        <div className="text-xs text-slate-400">Loading map…</div>
      </div>
    );
  }

  const maxValue = Math.max(...Object.values(stateData).map(d => d.value), 0);
  const scale = colorMode === 'revenue' ? BLUE_SCALE : PROFIT_SCALE;

  const getFill = (code: string): string => {
    const d = stateData[code];
    if (!d) return '#f1f5f9';
    if (colorMode === 'profit') return getProfitColor(d.value);
    return getRevenueColor(d.value, maxValue);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setMousePos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  };

  return (
    <div className="relative" onMouseMove={handleMouseMove}>
      <svg viewBox={data.viewBox} className="w-full h-auto">
        {Object.entries(data.regions).map(([code, region]) => {
          const isHovered = hovered === code;
          return (
            <path
              key={code}
              d={region.path}
              fill={getFill(code)}
              stroke={isHovered ? '#1e40af' : '#94a3b8'}
              strokeWidth={isHovered ? 2 : 0.5}
              className="transition-colors cursor-pointer"
              onMouseEnter={() => setHovered(code)}
              onMouseLeave={() => setHovered(null)}
            />
          );
        })}
      </svg>

      {hovered && stateData[hovered] && (() => {
        const d = stateData[hovered];
        const name = data.regions[hovered]?.name || hovered;
        return (
          <div
            className="absolute bg-white rounded-lg shadow-lg px-3 py-2 border border-slate-200 pointer-events-none z-10"
            style={{ left: Math.min(mousePos.x + 12, 280), top: mousePos.y - 60 }}
          >
            <p className="text-sm font-bold text-slate-800">{name}</p>
            {d.orders != null && <p className="text-xs text-slate-600">{d.orders.toLocaleString()} orders</p>}
            {d.revenue != null && formatMoney && (
              <p className="text-xs font-semibold text-blue-600">{formatMoney(d.revenue)}</p>
            )}
            {d.margin != null && (
              <p className={`text-xs font-semibold ${d.margin >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                Margin: {d.margin.toFixed(1)}%
              </p>
            )}
            <p className="text-xs text-slate-500">{d.label}</p>
          </div>
        );
      })()}

      <div className="mt-2 flex items-center justify-center gap-1 text-xs text-slate-500">
        <span>{colorMode === 'profit' ? 'Loss' : 'Low'}</span>
        {scale.map((color, i) => (
          <div key={i} className="w-6 h-3 rounded-sm" style={{ backgroundColor: color }} />
        ))}
        <span>{colorMode === 'profit' ? 'High margin' : 'High'}</span>
      </div>
    </div>
  );
};

export default React.memo(RegionMap);
